/** Loads a lean navigation basemap, preserving route numbers and local labels. */

import { MAPBOX_TOKEN, MAP_STYLES } from '../core/config';

type Theme = keyof typeof MAP_STYLES;

const STRIP_SOURCES = new Set(['mapbox-traffic', 'mapbox-incidents']);

type LabelLang = 'en' | 'local';

const cache = new Map<string, object>();
const STYLE_CACHE_PREFIX = 'wayfarer.basemap.v3.';

function offlineStyle(key: string, fallback: string): object | string {
  try {
    const saved = JSON.parse(localStorage.getItem(STYLE_CACHE_PREFIX + key) || 'null');
    if (saved?.version === 8 && saved.sources && Array.isArray(saved.layers)) return saved;
  } catch { /* Storage may be unavailable or full on a head unit. */ }
  return fallback;
}

export async function loadStrippedStyle(
  theme: Theme,
  /** 'local' shows each place's own name - Thai inside Thailand. */
  labels: LabelLang = 'en',
  timeoutMs = 8_000,
): Promise<object | string> {
  const key = `${theme}:${labels}`;
  const cached = cache.get(key);
  // Hand out a fresh copy each time: mapbox-gl mutates the style object it is
  // given, so returning the cached instance twice feeds it a poisoned style on
  // the second theme switch.
  if (cached) return clone(cached);

  const url = MAP_STYLES[theme];
  try {
    const httpUrl =
      url.replace('mapbox://styles/', 'https://api.mapbox.com/styles/v1/') +
      `?access_token=${MAPBOX_TOKEN}`;
    const res = await fetch(httpUrl, { signal: AbortSignal.timeout(timeoutMs) });
    if (!res.ok) return offlineStyle(key, url);
    const style = (await res.json()) as MapboxStyle;

    if (style.sources) {
      for (const name of STRIP_SOURCES) delete style.sources[name];
    }
    if (style.layers) {
      style.layers = style.layers.filter((l) => !l.source || !STRIP_SOURCES.has(l.source));
    }

    simplifyNavigationStyle(style, theme);
    if (labels === 'local') preferLocalNames(style);

    cache.set(key, style as object);
    try { localStorage.setItem(STYLE_CACHE_PREFIX + key, JSON.stringify(style)); } catch { /* Optional offline cache. */ }
    return clone(style as object);
  } catch {
    return offlineStyle(key, url);
  }
}

/**
 * Identify basemap symbol layers that render street/road names.
 *
 * Exported for MapView's post-load safety pass: when fetching the style JSON
 * fails, mapbox-gl loads the raw mapbox:// style URL and this loader never gets
 * a chance to rewrite it. The live style can then be inspected and the same
 * text fields blanked with setLayoutProperty.
 */
export function roadNameLayerIds(style: unknown): string[] {
  if (!style || typeof style !== 'object') return [];
  const layers = (style as MapboxStyle).layers ?? [];
  return layers.filter(isRoadNameLayer).flatMap((layer) => (layer.id ? [layer.id] : []));
}

function applyRoadNamePolicy(style: MapboxStyle): void {
  for (const layer of style.layers ?? []) {
    if (!isRoadNameLayer(layer) || !layer.layout) continue;
    const layout = layer.layout;
    if (layout['symbol-placement'] && !layout['icon-image']) {
      // The driver restored English major-road names, using Mapbox's wording.
      // Never synthesize a translation or fall back to an unsigned local name.
      layout['text-field'] = ['case', ['match', ['get', 'class'], ['motorway', 'trunk', 'primary'], true, false], ['coalesce', ['get', 'name_en'], ''], ''];
      layout['text-size'] = 16;
      layout['symbol-spacing'] = 500;
      layout['text-font'] = ['DIN Pro Bold', 'Arial Unicode MS Bold'];
      layout['text-padding'] = 10;
      layout['text-allow-overlap'] = false;
    } else {
      // Intersection icons remain; their separate street-name text does not.
      layout['text-field'] = '';
    }
  }
}

function isRoadNameLayer(layer: MapboxLayer): boolean {
  if (layer.type !== 'symbol' || layer['source-layer'] !== 'road') return false;
  if (!readsNameField(layer.layout?.['text-field'])) return false;

  // Mapbox groups ferry and aerialway labels into the road source. They name a
  // transport feature, not a street, and remain useful when no road is present.
  // Check the layer's semantic id, not arbitrary filter text: an ordinary road
  // label can mention `ferry` only to exclude that class.
  return !containsTransportKind(layer.id);
}

function readsNameField(node: unknown): boolean {
  if (typeof node === 'string') return /\{name(?:_[^}]*)?\}/i.test(node);
  if (Array.isArray(node)) {
    if (node[0] === 'get' && typeof node[1] === 'string' && /^name(?:_|$)/i.test(node[1])) {
      return true;
    }
    return node.some(readsNameField);
  }
  if (node && typeof node === 'object') {
    return Object.values(node as Record<string, unknown>).some(readsNameField);
  }
  return false;
}

function containsTransportKind(id: string | undefined): boolean {
  return /(?:^|[-_ ])(?:ferry|aerialway)(?:$|[-_ ])/i.test(id ?? '');
}

/**
 * Flip label priority from English-first to local-first.
 *
 * Every `["get", "name_en"]` anywhere in a text-field becomes
 * `["coalesce", ["get","name"], ["get","name_en"]]`. Done as a substitution on
 * the whole expression tree rather than by matching the one shape the style
 * happens to use today: the layers here already use four different shapes
 * (plain coalesce, `step` on sizerank, `step` on zoom, nested), and a
 * shape-specific rewrite would silently miss the ones it did not anticipate,
 * leaving a map that is Thai in most places and English in a few.
 *
 * The replacement is not descended into, so it cannot rewrite itself.
 */
function preferLocalNames(style: MapboxStyle): void {
  for (const layer of style.layers ?? []) {
    if (isRoadNameLayer(layer)) continue; // Major-road names stay English in Thai UI too.
    const layout = (layer as { layout?: Record<string, unknown> }).layout;
    if (!layout || layout['text-field'] === undefined) continue;
    layout['text-field'] = localise(layout['text-field']);
  }
}

function localise(node: unknown): unknown {
  if (!Array.isArray(node)) return node;
  if (node.length === 2 && node[0] === 'get' && node[1] === 'name_en') {
    return ['coalesce', ['get', 'name'], ['get', 'name_en']];
  }
  return node.map(localise);
}

interface MapboxLayer {
  id?: string;
  type?: string;
  source?: string;
  'source-layer'?: string;
  filter?: unknown;
  minzoom?: number;
  layout?: Record<string, unknown>;
  paint?: Record<string, unknown>;
}

interface MapboxStyle {
  metadata?: Record<string, unknown>;
  sources?: Record<string, unknown>;
  layers?: MapboxLayer[];
}

function clone<T>(v: T): T {
  return typeof structuredClone === 'function'
    ? structuredClone(v)
    : (JSON.parse(JSON.stringify(v)) as T);
}

/**
 * Reverse the decorative road-border treatment: fewer actual layers, flat
 * colours and no scenery textures keep weak head units focused on navigation.
 * The live-style safety pass uses this too after a timed-out JSON fetch.
 */
export function simplifyNavigationStyle(style: MapboxStyle, theme: Theme): void {
  style.metadata = { ...style.metadata, 'wayfarer:lean-map': 1 };
  applyRoadNamePolicy(style);
  const night = theme === 'night';
  const palette = night
    ? { land: '#101820', water: '#223b4b', road: '#8a969e', text: '#f1f5f7' }
    : { land: '#f1efe8', water: '#b3d2df', road: '#646e73', text: '#17232b' };
  style.layers = (style.layers ?? []).filter((layer) => {
    const id = layer.id ?? '';
    if (id.startsWith('wf-')) return true;
    if (layer.source && STRIP_SOURCES.has(layer.source)) return false;
    if (['fill-extrusion', 'hillshade', 'sky'].includes(layer.type ?? '')) return false;
    if (/building|landuse|landcover|national-park|hillshade|contour|poi|transit|aeroway/i.test(id)) return false;
    if (layer.type === 'line' && /road|bridge|tunnel/i.test(id) && /case|casing|outline/i.test(id)) return false;
    return true;
  });
  for (const layer of style.layers) {
    if ((layer.id ?? '').startsWith('wf-')) continue;
    const id = layer.id ?? '';
    const paint = layer.paint ??= {};
    if (layer.type === 'background') paint['background-color'] = palette.land;
    if (layer.type === 'fill') {
      paint['fill-color'] = /water/i.test(id) ? palette.water : palette.land;
      delete paint['fill-pattern'];
      delete paint['fill-outline-color'];
    }
    if (layer.type === 'line' && /road|bridge|tunnel/i.test(id)) {
      paint['line-color'] = palette.road;
      if (!/rail|ferry|path|pedestrian|steps/i.test(id)) {
        const major = /motorway|trunk|primary/i.test(id);
        // Broader roads at overview/cruise zoom, with normal scale near a turn.
        // One zoom expression on existing layers adds no geometry or draw calls.
        paint['line-width'] = major
          ? ['interpolate', ['linear'], ['zoom'], 5, 1.5, 10, 3, 13, 5, 15, 9, 18, 22, 22, 70]
          : ['interpolate', ['linear'], ['zoom'], 5, 1, 10, 1.6, 13, 2.5, 15, 5, 18, 15, 22, 50];
      }
      // Keep local streets at driving zoom, without drawing every alley in a
      // city-wide overview. Main roads and access ramps stay visible earlier.
      if (/(?:street|minor)/i.test(id)) layer.minzoom = Math.max(layer.minzoom ?? 0, 14);
    }
    if (layer.type !== 'symbol') continue;
    const layout = layer.layout ??= {};
    if (/shield/i.test(id)) {
      // Keep the backing plate compact while enlarging its numbers. Preserve
      // its original ink so light text never vanishes on a light sprite.
      layout['text-size'] = 18;
      layout['icon-size'] = 1;
      layout['text-padding'] = 12;
      layout['icon-padding'] = 12;
      layout['text-allow-overlap'] = false;
      layout['icon-allow-overlap'] = false;
      // Mapbox uses a zoom expression switching point -> line placement.
      // Set spacing for that expression too, not only the literal 'line'.
      if (!/exit/i.test(id)) layout['symbol-spacing'] = ['interpolate', ['linear'], ['zoom'], 10, 650, 18, 900, 22, 1600];
    } else {
      paint['text-color'] = palette.text;
      paint['text-halo-color'] = palette.land;
      paint['text-halo-width'] = 1.5;
    }
  }
  // Placement runs from the topmost symbol layer down. Stock number shields
  // and place labels otherwise consume the space before road names get a turn.
  const roadLabels = style.layers.filter(isRoadNameLayer);
  const roadLabelSet = new Set(roadLabels);
  style.layers = [...style.layers.filter((layer) => !roadLabelSet.has(layer)), ...roadLabels];
}
